import { Button, CircularProgress, DialogActions, TextField, Typography } from "@mui/material";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeParams } from "primereact/dropdown";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useResultMeasurementStatusUpdate } from "../data/Results/useResultMeasurementStatusUpdate";
import { FormRow } from "../shared/FormLayout/FormRow";
import { Measurement, MeasurementDetail } from "../shared/interfaces/Result";
import RAGStatusChip from "./RAGStatusChip";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { RAGStatus } from "../shared/enums/Result";

interface MeasurementStatusEditorDialogProps {
  open: boolean;
  measurementDetail: MeasurementDetail<Measurement>;
  captureId: number;
  customerId: number;
  onCancel: () => void;
  onEdited: () => void;
}

export function MeasurementStatusEditorDialog(props: MeasurementStatusEditorDialogProps) {
  const { t } = useTranslation();

  const [selectedStatus, setSelectedStatus] = React.useState(props.measurementDetail.measurement.RAGStatus);
  const [comment, setComment] = React.useState("");

  const [showErrorToast, setShowErrorToast] = useState(false);

  const statusOptions = [RAGStatus.Red, RAGStatus.Amber, RAGStatus.Green];

  const handleStatusChange = (e: DropdownChangeParams) => {
    setSelectedStatus(e.value);
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
  };

  const handleUpdateClick = () => {
    updateMeasurementStatus.mutate({
      ragStatus: selectedStatus,
      oldRagStatus: props.measurementDetail.measurement.RAGStatus,
      comment: comment,
      captureId: props.captureId,
      customerId: props.customerId,
      panel360AssetId: props.measurementDetail.panel360AssetId,
      panelNo: props.measurementDetail.panelNo,
      measurementType: props.measurementDetail.measurementType,
      component: props.measurementDetail.measurement.ComponentType,
      subLocation: props.measurementDetail.measurement.SubLocation,
    });
  };

  const updateMeasurementStatus = useResultMeasurementStatusUpdate({
    onSuccess: handleUpdateMeasurementStatusSuccess,
    onError: handleUpdateMeasurementStatusError,
  });

  function handleUpdateMeasurementStatusSuccess() {
    props.onEdited();
  }

  function handleUpdateMeasurementStatusError() {
    setShowErrorToast(true);
  }

  const hasStatusChanged = selectedStatus !== props.measurementDetail.measurement.RAGStatus;

  return (
    <Dialog
      header={
        <Typography variant="h6" component="p">
          {t("common.status")}
        </Typography>
      }
      visible={props.open}
      onHide={props.onCancel}
    >
      {updateMeasurementStatus.isLoading ? (
        <div className="d-flex justify-content-center">
          <CircularProgress size={65} />
        </div>
      ) : (
        <>
          <FormRow
            label={t("common.status")}
            disableDivider
            value={
              <Dropdown
                value={selectedStatus}
                options={statusOptions}
                itemTemplate={(item) => <RAGStatusChip ragStatus={item} />}
                valueTemplate={(item) => <RAGStatusChip ragStatus={item} />}
                onChange={handleStatusChange}
              />
            }
          />

          {hasStatusChanged && (
            <FormRow
              label={t("common.comments")}
              value={<TextField multiline value={comment} onChange={handleCommentChange} />}
            />
          )}

          <DialogActions>
            <Button variant="outlined" onClick={props.onCancel}>
              {t("common.cancel")}
            </Button>
            <Button variant="contained" onClick={handleUpdateClick} disabled={!hasStatusChanged || !comment}>
              {t("common.update")}
            </Button>
          </DialogActions>
          <div>
            {showErrorToast && (
              <MessageToast
                text={t("common.dataUpdateError", { val: "$t(common.status)" })}
                mode={ToastMode.Error}
                show={showErrorToast}
                autoHide
              ></MessageToast>
            )}
          </div>
        </>
      )}
    </Dialog>
  );
}
